import { cva, type VariantProps } from "./style.utils";

// -----------------------------------------------------------------------------

export type AccordionRootVariants = VariantProps<typeof accordionRootVariants>;

export const accordionRootVariants = cva({
  base: "",
  variants: {
    itemDivider: {
      unset: null,
      neutral: "divide-y divide-black/10",
      white: "divide-y divide-white/15",
    },
    itemSpacing: {
      unset: null,
      md: "*:py-18px",
      lg: "*:py-36px",
    },
  },
  defaultVariants: {
    itemDivider: "neutral",
    itemSpacing: "md",
  },
});

// -----------------------------------------------------------------------------

export type AccordionItemVariants = VariantProps<typeof accordionItemVariants>;

export const accordionItemVariants = cva({});

// -----------------------------------------------------------------------------

export type AccordionHeadingVariants = VariantProps<
  typeof accordionHeadingVariants
>;

export const accordionHeadingVariants = cva({
  base: "flex",
});

// -----------------------------------------------------------------------------

export type AccordionTriggerVariants = VariantProps<
  typeof accordionTriggerVariants
>;

export const accordionTriggerVariants = cva({
  base: [
    // layout
    "group flex items-center gap-x-5px",
    // animation
    "transition-all",
    // focus/hover/active
    "hover:underline",
    // focus unset outline
    "focus:outline-none",
    // focus reset outline (keyboard only)
    "focus-visible:rounded-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ring-normal",
  ],
  variants: {
    palette: {
      unset: null,
      accent: [
        // foreground
        "text-link-idle",
        // focus/hover/active
        "hover:text-link-hover focus:text-link-focus active:text-link-active",
      ],
      neutral: [
        // foreground
        "text-shade-100",
        // focus/hover/active
        "hover:text-shade-80 focus:text-shade-80 active:text-shade-80",
      ],
      white: [
        // foreground
        "text-white",
        // focus/hover/active
        "hover:text-white/80 focus:text-white/80 active:text-white/80",
        // focus reset outline (keyboard only)
        "focus-visible:outline-ring-inverse",
      ],
    },
    size: {
      unset: null,
      sm: "text-sm",
      md: "text-md",
    },
    iconPlacement: {
      unset: null,
      start: "flex-row-reverse gap-5px font-semibold last:*:-translate-x-5px",
      end: "flex-1 justify-between last:*:translate-x-5px",
    },
    iconPalette: {
      unset: null,
      neutral: "last:*:text-shade-100/40",
      white: "last:*:text-white/60",
    },
  },
  defaultVariants: {
    palette: "accent",
    size: "md",
    iconPlacement: "end",
    iconPalette: "neutral",
  },
});

// -----------------------------------------------------------------------------

export type AccordionIconVariants = VariantProps<typeof accordionIconVariants>;

export const accordionIconVariants = cva({
  base: [
    // layout
    "shrink-0",
    // animation
    "transition-transform duration-200 group-[[data-state=open]]:rotate-180",
  ],
});

// -----------------------------------------------------------------------------

export type AccordionContentVariants = VariantProps<
  typeof accordionContentVariants
>;

export const accordionContentVariants = cva({
  // Do NOT add padding here, add it to children (affects animations)
  base: [
    // layout
    "overflow-hidden",
    // foreground
    "text-sm",
    // animation
    "data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down",
  ],
  variants: {
    indent: {
      false: null,
      true: "*:pl-22px",
    },
    size: {
      unset: null,
      sm: "*:pt-10px",
      md: "*:pt-16px",
    },
  },
  defaultVariants: {
    indent: false,
    size: "md",
  },
});
